import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Layout from "../../Layout.tsx";
import { getTerminals } from "../../../data/terminals";
import { Terminal } from "../../../types/Terminal";
import { showModal } from "../../../data/modal";
import Modal from "../../Modal.tsx";

import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";

import {
  Close as CloseIcon,
  Edit as EditIcon,
  Map as MapIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import SettingsMenu from "../Menu";

const getTerminalIdentity = (terminal: Terminal, index: number) => {
  return (
    (terminal as any)["@id"] ??
    terminal.id ??
    `${terminal.abbreviation ?? "terminal"}-${terminal.name ?? "unknown"}-${index}`
  ).toString();
};

const TerminalManage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const terminals = useSelector((state: any) => state.terminals);

  const [searchTerm, setSearchTerm] = useState("");

  const refreshTerminals = () => {
    dispatch(getTerminals() as any);
  };

  useEffect(() => {
    refreshTerminals();
  }, [dispatch]);

  useEffect(() => {
    const handleTerminalSaved = () => {
      refreshTerminals();
    };

    window.addEventListener("terminal-crud:saved", handleTerminalSaved);

    return () => {
      window.removeEventListener("terminal-crud:saved", handleTerminalSaved);
    };
  }, [dispatch]);

  const terminalData: Terminal[] = useMemo(() => {
    const rawData: Terminal[] = Array.isArray(terminals?.data)
      ? terminals.data
      : [];

    const seen = new Set<string>();

    return rawData.filter((terminal: Terminal, index: number) => {
      const key = getTerminalIdentity(terminal, index);

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }, [terminals?.data]);

  const filteredTerminals = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return terminalData;
    }

    return terminalData.filter((terminal: Terminal) => {
      const name = terminal.name?.toLowerCase() ?? "";
      const abbreviation = terminal.abbreviation?.toLowerCase() ?? "";
      const city = terminal.city?.toLowerCase() ?? "";
      const country = (terminal as any).country?.toLowerCase?.() ?? "";

      const aliases = Array.isArray((terminal as any).aliases)
        ? (terminal as any).aliases
            .map((alias: string) => alias?.toLowerCase?.() ?? "")
            .join(" ")
        : "";

      return (
        name.includes(normalizedSearchTerm) ||
        abbreviation.includes(normalizedSearchTerm) ||
        city.includes(normalizedSearchTerm) ||
        country.includes(normalizedSearchTerm) ||
        aliases.includes(normalizedSearchTerm)
      );
    });
  }, [terminalData, searchTerm]);

  const formatAliases = (aliases?: string[] | null) => {
    if (!Array.isArray(aliases) || aliases.length === 0) {
      return "";
    }

    return aliases.join(", ");
  };

  const renderColorIndicator = (color?: string | null) => {
    if (!color) {
      return null;
    }

    return (
      <Box
        title={color}
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          backgroundColor: color,
          border: "1px solid rgba(0, 0, 0, 0.25)",
          display: "inline-block",
          verticalAlign: "middle",
        }}
      />
    );
  };

  const openAddTerminalModal = () => {
    dispatch(
      showModal({
        title: t("addTerminal"),
        description: t("addTerminal"),
        form: "terminal",
        formProps: {
          mode: "create",
          terminal: null,
          modalKey: `terminal-create-${Date.now()}`,
        },
        size: "default",
      }) as any
    );
  };

  const openEditTerminalModal = (terminal: Terminal) => {
    dispatch(
      showModal({
        title: t("editTerminal"),
        description: t("editTerminal"),
        form: "terminal",
        formProps: {
          terminal,
          mode: "edit",
          modalKey:
            (terminal as any)["@id"] ??
            terminal.id?.toString() ??
            `terminal-edit-${Date.now()}`,
        },
        size: "default",
      }) as any
    );
  };

  const openTerminalMapModal = (terminal: Terminal) => {
    dispatch(
      showModal({
        title: t("showMap"),
        description: t("showMap"),
        form: "terminalmapmodal",
        formProps: {
          terminal,
          modalKey:
            (terminal as any)["@id"] ??
            terminal.id?.toString() ??
            `terminal-map-${Date.now()}`,
        },
        size: "default",
      }) as any
    );
  };

  return (
    <Layout>
      <Modal />

      <Grid container>
        <Grid item xs={12} sm={2} padding={1}>
          <Grid item component={Paper} sx={{ height: "auto" }}>
            <SettingsMenu activeMenu={"terminal"} />
          </Grid>
        </Grid>

        <Grid item xs={12} sm={10} padding={1}>
          <Grid item component={Paper} sx={{ height: "auto" }}>
            <Grid
              className="settings-page-header"
              item
              xs={12}
              sm={12}
              padding={1}
              container
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t("searchTerminal")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton
                          type="button"
                          size="small"
                          onClick={() => setSearchTerm("")}
                          edge="end"
                          title={t("clearSearch")}
                          aria-label={t("clearSearch")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>

              <Grid item>
                <Button
                  type="button"
                  onClick={openAddTerminalModal}
                  variant="contained"
                  size="small"
                >
                  {t("addTerminal")}
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={12} padding={1}>
              <TableContainer sx={{ height: "80vh", overflow: "auto" }}>
                <Table aria-label={t("terminal")} stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("abbreviation")}</TableCell>
                      <TableCell>{t("name")}</TableCell>
                      <TableCell align="center">{t("aliases")}</TableCell>
                      <TableCell align="center">{t("city")}</TableCell>
                      <TableCell align="center">{t("color")}</TableCell>
                      <TableCell align="center">{t("location")}</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredTerminals.map((terminal: Terminal, index) => (
                      <TableRow key={getTerminalIdentity(terminal, index)}>
                        <TableCell align="center">
                          {terminal.abbreviation ?? ""}
                        </TableCell>

                        <TableCell>{terminal.name ?? ""}</TableCell>

                        <TableCell align="center">
                          {formatAliases((terminal as any).aliases)}
                        </TableCell>

                        <TableCell align="center">
                          {terminal.city ?? ""}
                        </TableCell>

                        <TableCell align="center">
                          {renderColorIndicator(terminal.color)}
                        </TableCell>

                        <TableCell align="center">
                          <IconButton
                            type="button"
                            title={t("showMap")}
                            aria-label={t("showMap")}
                            onClick={() => openTerminalMapModal(terminal)}
                          >
                            <MapIcon fontSize="small" />
                          </IconButton>
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            type="button"
                            title={t("editTerminal")}
                            aria-label={t("editTerminal")}
                            onClick={() => openEditTerminalModal(terminal)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}

                    {filteredTerminals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          {t("noMatches")}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default TerminalManage;